import { useEffect, useState } from "react";

import CircularProgress from "@mui/material/CircularProgress";
import { HexColorPicker, HexColorInput } from "react-colorful";
import ReactSelect from "react-select";
import { MoveUp, MoveDown, Plus, Pencil, X } from "lucide-react";
import toast from "react-hot-toast";

import { useRole } from "../../components/MangeContextProvide";
import SearchInput from "../../common/SearchInput";
import {
    FromDataType,
    GroupingErrors,
    ManualMilestonesState,
    MilestoneOption,
} from "./grouping-milestone";
import {
    getManualMilestones,
    saveGrouping,
    toggleGroupingActive,
} from "./Groupmilestones.service";
import { useGroupMilestones } from "./Usegroupmilestones";

/*
 * NOTE: the .ctpl-* classes come from the shared CommunicationTemplate.css.
 * Plain CSS imports are global once imported anywhere, so as long as a
 * CommunicationTemplate component is in the bundle these styles already apply.
 * If you want this file self-contained, uncomment the line below and point it
 * at wherever that stylesheet lives:
 *
 * import "../ManageCommunicationTemplete/CommunicationTemplate.css";
 */

// ── react-select styled to sit inside the ctpl amber/blue palette ──────────
const reactSelectStyles: any = {
    control: (base: any, state: any) => ({
        ...base,
        minHeight: 44,
        borderRadius: 8,
        backgroundColor: "#fff",
        borderColor: state.isFocused ? "#f5a623" : "#e4e7ec",
        boxShadow: state.isFocused ? "0 0 0 3px #fff3dc" : "none",
        "&:hover": { borderColor: "#c2cad6" },
    }),
    valueContainer: (base: any) => ({
        ...base,
        flexWrap: "wrap",
        rowGap: "6px",
        padding: "6px 10px",
    }),
    multiValue: (base: any) => ({
        ...base,
        backgroundColor: "#eaf1ff",
        borderRadius: "7px",
        overflow: "hidden",
    }),
    multiValueLabel: (base: any) => ({
        ...base,
        color: "#3b78e0",
        fontWeight: 600,
        fontSize: "0.78rem",
        padding: "4px 6px",
    }),
    multiValueRemove: (base: any) => ({
        ...base,
        color: "#3b78e0",
        ":hover": { backgroundColor: "#d7e5ff", color: "#0d47a1" },
    }),
    indicatorsContainer: (base: any) => ({ ...base, alignSelf: "stretch" }),
    clearIndicator: (base: any) => ({
        ...base,
        color: "#aaa",
        padding: 8,
        cursor: "pointer",
        ":hover": { color: "#d92d20" },
    }),
    dropdownIndicator: (base: any) => ({
        ...base,
        color: "#aaa",
        padding: 8,
        cursor: "pointer",
        ":hover": { color: "#f5a623" },
    }),
    indicatorSeparator: (base: any) => ({
        ...base,
        backgroundColor: "#e0e0e0",
        marginTop: 8,
        marginBottom: 8,
    }),
    menu: (base: any) => ({ ...base, zIndex: 9999, borderRadius: 8, overflow: "hidden" }),
    option: (base: any, state: any) => ({
        ...base,
        fontSize: "0.9rem",
        backgroundColor: state.isFocused ? "#fff3dc" : "#fff",
        color: "#1f2430",
        "&:active": { backgroundColor: "#ffe6ad" },
    }),
    placeholder: (base: any) => ({ ...base, color: "#98a2b3", fontSize: "0.9rem" }),
};

const DEFAULT_COLOR = "#156082";

function GroupMilestones() {
    const { globleCustomerId } = useRole() as any;

    // table data + search (via hook)
    const {
        milestoneData,
        loading,
        setSearchQuery,
        debouncedSearch,
        fetchMilestones,
    } = useGroupMilestones();

    // single modal + form state
    const [openGroup, setOpenGroup] = useState(false);
    const [firstMilestone, setFirstMilestone] = useState<MilestoneOption[]>([]);
    const [lastMilestone, setLastMilestone] = useState<MilestoneOption[]>([]);
    const [editingId, setEditingId] = useState<string | null>(null);

    const [color, setColor] = useState(DEFAULT_COLOR);
    const [showPicker, setShowPicker] = useState(false);
    const [milestone, setMilestone] = useState("first");
    const [type, setType] = useState("Air");

    const [formData, setFromData] = useState<FromDataType>({
        groupName: "",
        colorCode: DEFAULT_COLOR,
        description: "",
        type: "Air",
    });

    const [manualMilestones, setManualMilestones] = useState<ManualMilestonesState>({
        airfirstMilestones: [],
        airlastMilestones: [],
        oceanfirstMilestones: [],
        oceanlastMilestones: [],
    });

    const [errors, setErrors] = useState<GroupingErrors>({
        groupName: "",
        description: "",
        milestones: "",
        type: "",
    });

    const firstOptions = [
        { value: 10, label: "Air" },
        { value: 20, label: "Ocean" },
    ];

    /* ── single source of truth for clearing the whole form ──────────────── */
    const resetForm = () => {
        setFromData({
            groupName: "",
            colorCode: DEFAULT_COLOR,
            description: "",
            type: "Air",
        });
        setColor(DEFAULT_COLOR);
        setFirstMilestone([]);
        setLastMilestone([]);
        setEditingId(null);
        setType("Air");
        setMilestone("first");
        setShowPicker(false);
        setErrors({ groupName: "", description: "", milestones: "", type: "" });
    };

    // Open a fresh "create" form (always starts clean)
    const handleOpenAdd = () => {
        resetForm();
        setOpenGroup(true);
    };

    // Close + fully reset (used by Cancel, X, and overlay)
    const handleClose = () => {
        setOpenGroup(false);
        resetForm();
    };

    /* ── handlers (logic unchanged, axios moved to service) ──────────────── */

    const handleChangeSwitch = async (row: any) => {
        try {
            const data = await toggleGroupingActive(row._id, !row.isActive);
            toast.success(data.message);
            fetchMilestones(debouncedSearch);
        } catch (error) {
            toast.error("something went wrong");
        }
    };

    const validateForm = () => {
        let newErrors = { groupName: "", description: "", milestones: "", type: "" };
        if (!formData.groupName.trim()) newErrors.groupName = "Group name is required";
        if (!formData.description.trim())
            newErrors.description = "Description is required";
        if (!formData.type.trim()) newErrors.type = "type is required";
        if (firstMilestone.length === 0 && lastMilestone.length === 0)
            newErrors.milestones = "At least one milestone must be selected";

        setErrors(newErrors);
        return !Object.values(newErrors).some((err) => err);
    };

    const handleEdit = (id: string, group: any) => {
        setEditingId(id);
        setFirstMilestone(
            group?.firstmilestones.map((item: any) => ({ label: item.name, value: item })) || []
        );
        setLastMilestone(
            group?.lastmilestones.map((item: any) => ({ label: item.name, value: item })) || []
        );
        setFromData((prev) => ({
            ...prev,
            groupName: group.groupName,
            description: group.description,
            type: group.type,
            colorCode: group.colorCode,
        }));
        setColor(group.colorCode || DEFAULT_COLOR);
        setMilestone("first");
        setShowPicker(false);
        setErrors({ groupName: "", description: "", milestones: "", type: "" });
        if (group.type == "air") {
            setType("Air");
        } else if (group.type == "ocean") {
            setType("Ocean");
        }
        setOpenGroup(true);
    };

    const handleMilestone = async (miletype: string) => {
        if (miletype === "Air") {
            const first = await getManualMilestones("Air", "first");
            setManualMilestones((prev) => ({ ...prev, airfirstMilestones: first }));

            const last = await getManualMilestones("Air", "last");
            setManualMilestones((prev) => ({ ...prev, airlastMilestones: last }));
        } else if (miletype === "Ocean") {
            const first = await getManualMilestones("Ocean", "first");
            setManualMilestones((prev) => ({ ...prev, oceanfirstMilestones: first }));

            const last = await getManualMilestones("Ocean", "last");
            setManualMilestones((prev) => ({ ...prev, oceanlastMilestones: last }));
        }
    };

    const handleType = (value: string) => {
        setType(value);
        setFromData((prev) => ({ ...prev, type: value }));
        setFirstMilestone([]);
        setLastMilestone([]);
    };

    useEffect(() => {
        handleMilestone("Air");
        handleMilestone("Ocean");
    }, []);

    const moveItem = (index: number, direction: "up" | "down") => {
        if (milestone == "first") {
            const newItems = [...firstMilestone];
            const targetIndex = direction === "up" ? index - 1 : index + 1;
            if (targetIndex < 0 || targetIndex >= newItems.length) return;
            [newItems[index], newItems[targetIndex]] = [newItems[targetIndex], newItems[index]];
            setFirstMilestone(newItems);
        } else if (milestone == "last") {
            const newItems = [...lastMilestone];
            const targetIndex = direction === "up" ? index - 1 : index + 1;
            if (targetIndex < 0 || targetIndex >= newItems.length) return;
            [newItems[index], newItems[targetIndex]] = [newItems[targetIndex], newItems[index]];
            setLastMilestone(newItems);
        }
    };

    const handleChangeGroup = (e: any, name: string) => {
        let value = e.target.value;
        if (name === "groupName") value = value.replace(/[^a-zA-Z0-9\s]/g, "");
        setFromData((prev) => ({ ...prev, [name]: value }));
    };

    async function handleAddGrouping() {
        if (!validateForm()) return;
        try {
            const payload = {
                colorCode: color || formData.colorCode,
                groupName: formData.groupName,
                description: formData.description,
                firstmilestones: firstMilestone.map((el: any) => el.value._id.toString()),
                lastmilestones: lastMilestone.map((el: any) => el.value._id.toString()),
                type: formData.type.toLowerCase(),
            };

            const data = await saveGrouping(payload, editingId);

            fetchMilestones("");
            toast.success(data.message);

            setOpenGroup(false);
            resetForm();
        } catch (err: any) {
            const error = err?.response?.data?.error || "Server error.";
            toast.error(error);
        }
    }

    /* ── derived data for the milestone section ──────────────────────────── */

    const orderingList = milestone === "first" ? firstMilestone : lastMilestone;

    const currentOptions: MilestoneOption[] = (
        milestone === "first"
            ? type === "Air"
                ? manualMilestones.airfirstMilestones
                : manualMilestones.oceanfirstMilestones
            : type === "Air"
                ? manualMilestones.airlastMilestones
                : manualMilestones.oceanlastMilestones
    ).map((item: any) => ({ label: item.name, value: item }));

    const handleSelectChange = (options: any) => {
        // remove duplicates by value._id
        const unique = options.filter(
            (v: any, i: number, a: any[]) =>
                a.findIndex((t: any) => t.value._id === v.value._id) === i
        );
        if (milestone === "first") setFirstMilestone(unique);
        else setLastMilestone(unique);
    };

    /* ── render ──────────────────────────────────────────────────────────── */

    return (
        <div>
          
            {/* Card: toolbar + table */}
            <div className="ctpl-card">
                <div className="ctpl-toolbar">
                    <SearchInput
                        placeholder="Search Grouping"
                        onChange={setSearchQuery}
                        width={340}
                    />
                    <div className="ctpl-spacer" />
                    <button className="ctpl-btn ctpl-btn-primary" onClick={handleOpenAdd}>
                        <Plus size={16} /> Add Grouping
                    </button>
                </div>

                <div className="ctpl-table-scroll">
                    <table className="ctpl-table">
                        <thead>
                            <tr>
                                <th>S.No</th>
                                <th>Group Name</th>
                                <th>Hex Color</th>
                                <th>Description</th>
                                <th>Milestones</th>
                                <th>Added on</th>
                                <th>Status</th>
                                <th style={{ textAlign: "right" }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={8} className="ctpl-empty-cell">
                                        <CircularProgress size={26} thickness={4} />
                                        <div style={{ marginTop: 10 }}>Loading groupings…</div>
                                    </td>
                                </tr>
                            ) : milestoneData.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="ctpl-empty-cell">
                                        No groupings found
                                    </td>
                                </tr>
                            ) : (
                                milestoneData.map((row: any, index: number) => {
                                    const canManage = globleCustomerId == row.customer;
                                    return (
                                        <tr key={row._id || index}>
                                            <td>{index + 1}</td>
                                            <td>
                                                <span className="ctpl-tmpl-name">{row.groupName}</span>
                                            </td>
                                            <td>
                                                <span
                                                    style={{
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        minWidth: 92,
                                                        height: 28,
                                                        padding: "0 10px",
                                                        borderRadius: 7,
                                                        color: "#fff",
                                                        fontFamily: "monospace",
                                                        fontSize: 12,
                                                        textShadow: "0 1px 2px rgba(0,0,0,.35)",
                                                        border: "1px solid rgba(0,0,0,.08)",
                                                        backgroundColor: row.colorCode,
                                                    }}
                                                >
                                                    {row.colorCode}
                                                </span>
                                            </td>
                                            <td>{row.description}</td>
                                            <td>{row.milestones}</td>
                                            <td>{row.addedOn}</td>
                                            <td>
                                                <button
                                                    className={`ctpl-switch ${row.isActive ? "on" : ""}`}
                                                    disabled={!canManage}
                                                    onClick={() => handleChangeSwitch(row)}
                                                    aria-label="Toggle status"
                                                    style={!canManage ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
                                                />
                                            </td>
                                            <td>
                                                <div className="ctpl-row-actions">
                                                    <button
                                                        className="ctpl-action-circle edit"
                                                        disabled={!row.isActive || !canManage}
                                                        onClick={() => row?._id && handleEdit(row._id, row)}
                                                        aria-label="Edit grouping"
                                                        style={
                                                            !row.isActive || !canManage
                                                                ? { opacity: 0.5, cursor: "not-allowed" }
                                                                : undefined
                                                        }
                                                    >
                                                        <Pencil size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ── Single modal: group details + milestones ─────────────────── */}
            <div
                className={`ctpl-overlay ${openGroup ? "open" : ""}`}
                onClick={handleClose}
            />
            <div className={`ctpl-drawer ${openGroup ? "open" : ""}`}>
                <div className="ctpl-drawer-header">
                    <h2>{editingId != null ? "Edit Grouping" : "Add Grouping"}</h2>
                    <button className="ctpl-icon-btn" onClick={handleClose}>
                        <X size={18} />
                    </button>
                </div>

                <div className="ctpl-drawer-body">
                    {/* Group details */}
                    <div className="ctpl-section-label">Group Details</div>

                    <div className="ctpl-field-group" style={{ marginBottom: 18 }}>
                        <label className="ctpl-field-label">Group Name</label>
                        <input
                            className="ctpl-field-input"
                            value={formData.groupName}
                            onChange={(e) => handleChangeGroup(e, "groupName")}
                        />
                        {errors.groupName && (
                            <div className="ctpl-field-error">{errors.groupName}</div>
                        )}
                    </div>

                    <div className="ctpl-field-group" style={{ marginBottom: 18 }}>
                        <label className="ctpl-field-label">Select Color</label>
                        <div style={{ position: "relative", width: "100%" }}>
                            <div
                                onClick={() => setShowPicker(true)}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    position: "relative",
                                    cursor: "pointer",
                                    padding: 5,
                                    border: "1px solid var(--ctpl-line)",
                                    borderRadius: 8,
                                }}
                            >
                                <div
                                    style={{
                                        width: "100%",
                                        height: 31,
                                        borderRadius: 4,
                                        backgroundColor: color,
                                    }}
                                />
                                <span
                                    style={{
                                        fontFamily: "monospace",
                                        color: "#fff",
                                        position: "absolute",
                                        inset: 0,
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        pointerEvents: "none",
                                        textShadow: "0 1px 2px rgba(0,0,0,.4)",
                                    }}
                                >
                                    {color}
                                </span>
                            </div>

                            {showPicker && (
                                <div
                                    style={{
                                        marginTop: 10,
                                        width: "100%",
                                        padding: "12px 15px",
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 8,
                                        position: "relative",
                                        border: "1px solid var(--ctpl-line)",
                                        borderRadius: 8,
                                    }}
                                >
                                    <button
                                        className="ctpl-icon-btn"
                                        onClick={() => setShowPicker(false)}
                                        style={{
                                            position: "absolute",
                                            top: -12,
                                            right: -12,
                                            background: "rgba(0,0,0,0.5)",
                                            color: "#fff",
                                            zIndex: 999,
                                        }}
                                    >
                                        <X size={16} />
                                    </button>
                                    <HexColorPicker
                                        color={color}
                                        onChange={setColor}
                                        style={{ width: "100%" }}
                                    />
                                    <HexColorInput
                                        color={color}
                                        onChange={setColor}
                                        prefixed
                                        className="ctpl-field-input"
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="ctpl-field-group" style={{ marginBottom: 22 }}>
                        <label className="ctpl-field-label">Description</label>
                        <textarea
                            className="ctpl-field-input"
                            rows={3}
                            style={{ resize: "vertical" }}
                            value={formData.description}
                            onChange={(e) => handleChangeGroup(e, "description")}
                        />
                        {errors.description && (
                            <div className="ctpl-field-error">{errors.description}</div>
                        )}
                    </div>

                    {/* Milestones */}
                    <div className="ctpl-section-label">Milestones</div>

                    <div className="ctpl-field-group" style={{ marginBottom: 18 }}>
                        <label className="ctpl-field-label">Select Air/Ocean</label>
                        <select
                            className="ctpl-field-input"
                            disabled={editingId != null}
                            value={type}
                            onChange={(e) => handleType(e.target.value)}
                        >
                            {firstOptions.map((opt) => (
                                <option key={opt.value} value={opt.label}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                        {errors.type && <div className="ctpl-field-error">{errors.type}</div>}
                    </div>

                    <div className="ctpl-tabs" style={{ padding: 0, marginBottom: 16 }}>
                        <button
                            className={`ctpl-tab-btn ${milestone === "first" ? "active" : ""}`}
                            onClick={() => setMilestone("first")}
                        >
                            First Mile
                        </button>
                        <button
                            className={`ctpl-tab-btn ${milestone === "last" ? "active" : ""}`}
                            onClick={() => setMilestone("last")}
                        >
                            Last Mile
                        </button>
                    </div>

                    <div className="ctpl-field-group" style={{ marginBottom: 0 }}>
                        <label className="ctpl-field-label">Select Milestone</label>
                        <ReactSelect<any, true>
                            isMulti
                            styles={reactSelectStyles}
                            name={milestone === "first" ? "firstmilestone" : "lastmilestone"}
                            options={currentOptions}
                            value={orderingList}
                            onChange={handleSelectChange}
                        />
                        {errors.milestones && (
                            <div className="ctpl-field-error">{errors.milestones}</div>
                        )}

                        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 16 }}>
                            {orderingList.map((item: any, index) => (
                                <div
                                    key={item.value._id}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        background: "var(--ctpl-primary-soft)",
                                        border: "1px solid #f3d190",
                                        borderRadius: 8,
                                        padding: "8px 12px",
                                    }}
                                >
                                    <span style={{ fontWeight: 500, color: "var(--ctpl-ink)", fontSize: 13.5 }}>
                                        {item.value.name}
                                    </span>
                                    <div style={{ display: "flex", gap: 4 }}>
                                        <button
                                            className="ctpl-icon-btn"
                                            onClick={() => moveItem(index, "up")}
                                            disabled={index === 0}
                                            style={index === 0 ? { opacity: 0.4, cursor: "not-allowed" } : undefined}
                                        >
                                            <MoveUp size={16} />
                                        </button>
                                        <button
                                            className="ctpl-icon-btn"
                                            onClick={() => moveItem(index, "down")}
                                            disabled={index === orderingList.length - 1}
                                            style={
                                                index === orderingList.length - 1
                                                    ? { opacity: 0.4, cursor: "not-allowed" }
                                                    : undefined
                                            }
                                        >
                                            <MoveDown size={16} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="ctpl-drawer-footer">
                    <button className="ctpl-btn ctpl-btn-ghost" onClick={handleClose}>
                        Cancel
                    </button>
                    <button className="ctpl-btn ctpl-btn-primary" onClick={handleAddGrouping}>
                        {editingId != null ? "Update" : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default GroupMilestones;